import RenderManager from '../rendering/render-manager';
import Network from '../miniviews/network';
import Analytics from '../analytics';
import AssessmentManager, { clearAssessments } from '../assessment/assessment-manager';
import * as EnvLight from './env-light';
import { fire, listen, unlisten } from './events';
import getVWKey from "../helpers/getVWKey";
import set2DMobilePosAndSize from "../helpers/set2DMobilePosAndSize";
import { goToNextVisibleSlide, goToPrevVisibleSlide, setNextPrevEnabled } from "../slides/add-slide-controls"
import AssetDeserializer from '../assets/asset-deserializer';
import Utils from '../utils';
import QuizManager from '../quizes/quiz-manager';
import AssetLibrary from '../assets/asset-library';
import LayersManager from '../layers/LayersManager';
import AssetsBusy from '../assets/assets-busy';
import { setRenderData } from '../helpers/get-renderdata';
import getLetterbox from '../helpers/get-letterbox';
import resize from './resize';
import LimitsCtl from '../../editor/components/Editor/Tools3D/CameraLimitsSubpanel/LimitsCtl';
import CameraTweenTrack from '../camera/camera-tween-track';
import { iterateTourActionContainers, iterateSlideActionContainers, iterateActions } from './iterate-actions';
import chainIterators from './chain-iterators';
import ActionReferenceMap from './action-ref-map';

let activeSlide = 0, prevSlide = -1;
let activeAudio = null;
let commitListener = null;
let callbacks = [];

const allowBusySlides = Utils.queryParams.has("force_slide_change");
const calloutsMode = Utils.queryParams.get("callouts") === "true";
const SlideManager = new class SlideManager {
    set activeSlideAudio(v) { activeAudio = v; }
    get activeSlideAudio() { return activeAudio; }

    get slides() { return Q3.data.slides; }
    get slidesObj() { return Q3.data.slides.reduce((acc, s) => { acc[s.uuid] = s; return acc }, {}); }
    getSlide(slideIndex) { return this.slides[slideIndex]; }

    get currentSlide() { return this.getSlide(activeSlide); }
    get activeSlide() { return activeSlide; }
    set activeSlide(slide) {
        activeSlide = slide;

        AssetDeserializer.loadSlideQueue({
            data: Q3.data,
            slide,
            assets: AssetLibrary
        });
    }

    get callbacks() { return callbacks; }
    get slideCount() { return this.slides.length; }
    get visibleSlideCount() { return this.slides.reduce((acc, s) => s.visible ? ++acc : acc, 0); }

    get prevSlide() { return prevSlide; }
    set prevSlide(slide) { prevSlide = slide; }

    reset() {
        deleteSlideLayers(activeSlide);

        prevSlide = -1;
        activeSlide = 0;
    }

    saveSlideView(slideIndex, cameraPosition) {
        if (!cameraPosition) return;

        const slide = this.getSlide(slideIndex);

        const slideCamera = Q3.mobileView
            ? slide.camera.sizeSm = slide.camera.sizeSm || { position: {}, target: {} }
            : slide.camera;

        Object.assign(slideCamera.position, {
            x: cameraPosition.position.x,
            y: cameraPosition.position.y,
            z: cameraPosition.position.z
        });

        Object.assign(slideCamera.target, {
            x: cameraPosition.target.x,
            y: cameraPosition.target.y,
            z: cameraPosition.target.z
        });

        // console.log(`${slideIndex} => position: (${cameraPosition.position.x.toFixed(2)}, ${cameraPosition.position.y.toFixed(2)}, ${cameraPosition.position.z.toFixed(2)}) | target: (${cameraPosition.target.x.toFixed(2)}, ${cameraPosition.target.y.toFixed(2)}, ${cameraPosition.target.z.toFixed(2)})`)

        slide.camera.gate = Network.currentWorldUuid;

        CameraTweenTrack.finishTweens();
    }

    waitForSlide(slideIndex) {
        return new Promise(resolve => {
            function waitForSlide() {
                if (!allowBusySlides && !AssetsBusy.isSlideReady(slideIndex)) {
                    requestAnimationFrame(waitForSlide);
                    return;
                }

                resolve(slideIndex);
            }

            waitForSlide();
        });
    }

    async setSlide(slideIndex, opts = {}) {
        if (slideIndex === null || slideIndex === undefined || (prevSlide === activeSlide && Q3.slideChangeDisabled)) return;
        if (Q3.teachMode === "student" && !opts.teachModeEvent && !opts.start) return;
        if (slideIndex < 0) slideIndex = this.slides.length - 1;
        if (slideIndex >= this.slides.length) slideIndex = 0;

        // prevents re-setting the slide if clicked to join the current slide,
        // from the editor UI, as well as from the labels
        if (slideIndex === activeSlide && !opts.force) return;

        prevSlide = activeSlide;

        const operationsBeforeLeaving = [];
        fire("leaveslide", { slideIndex: prevSlide, operationsBeforeLeaving });
        if (Q3.editMode) {
            await Promise.all(operationsBeforeLeaving);
        }

        Q3.data.updateLayers();

        this.activeSlide = slideIndex;

        if (!opts.byUndo && !opts.force) {
            if (opts.addedBefore) this.activeSlide = activeSlide + 1;
            fire("undo_slidechange");
        }

        if (!Q3.slideChangeDisabled) this.activeSlide = slideIndex;

        fire("enterslide", slideIndex);
        fire("slidechange", opts);

        if (Q3.inPreviewMode && !opts.external)
            parent.postMessage({ slide: Q3.SlideManager.activeSlide, type: "previewMessage" }, '*');

        setTimeout(() => Analytics.switchSlide(slideIndex, opts), 100);

        if (Q3.editMode) await commitToSlide(slideIndex, opts);
        else setSlideViewer(slideIndex, opts);
    }

    getFirstVisibleSlide() {
        let index = 0;

        while (this.slides[index].visible === false) {
            index++;

            if (index === this.slides.length)
                return null;
        }

        return index;
    }

    getPrevVisibleSlide(index) {
        index = typeof index === "number" ? index : this.activeSlide;

        while (this.slides[index].visible === false) {
            index--;

            if (index === this.slides.length)
                return null;
        }

        return index;
    }

    countHiddenSlidesBefore(index) {
        index = typeof index === "number" ? index : this.getPrevVisibleSlide();

        let count = 0;

        while (index) {
            this.slides[index].visible === false && count++;

            index--;
        }

        return count;
    }

}

export default SlideManager;
export { SlideManager };

const commitToSlide = (function () {

    function createNewLayers() {
        const layers2D = Q3.data.layers.layers2D;

        // create 2d layers of target slide
        const createdLayers = Object.keys(SlideManager.currentSlide.layers2D).reduce((accum, uuid) => {
            if (layers2D[uuid])
                accum[uuid] = layers2D[uuid];

            return accum;
        }, {});

        LayersManager.addLayers(createdLayers);

        let animDelayOnSlide = false;
        let viewerDelays = LayersManager.getEditorAnimationDelay(createdLayers, "viewer");
        if (Object.values(viewerDelays).reduce((acc, val) => acc + val, 0) > 0) {
            animDelayOnSlide = true;
        }
        return animDelayOnSlide;
    }

    function removeCameraLimits() {
        const _1deg = THREE.Math.DEG2RAD;

        // REMOVEING CAMERA LIMITS FOR PROPER TRANSITIONS
        Q3.controls.minAzimuthAngle = -Infinity;
        Q3.controls.maxAzimuthAngle = Infinity;
        Q3.controls.minDistance = 0;
        Q3.controls.maxDistance = Infinity;
        Q3.controls.minPolarAngle = _1deg;
        Q3.controls.maxPolarAngle = Math.PI - _1deg;
        Q3.controls.setPanLimits();
    }

    function triggerBackgroundTransitions(duration) {
        const curSlide = SlideManager.currentSlide;

        // SLIDE BACKGROUND TRANSITION
        RenderManager.background.setBackground(Object.assign(
            {}, curSlide.background, { tweenTime: duration }
        ));

        if (!Q3.editMode) {
            const style = Q3.container.parentElement.style;
            const colorValue = curSlide.background.value.color.primary;

            style.transitionDuration = `${duration}s`;
            style.backgroundColor = isColor(colorValue) ? colorValue : calloutsMode ? 'transparent' : "white";
        }

        function isColor(strColor) {
            const s = new Option().style;

            s.color = strColor;

            return s.color == strColor;
        }
    }

    function initCameraLimits(duration) {
        // CAMERA LIMITS
        Q3.setCameraLimits = () => {
            const cameraLimits = LimitsCtl.getLayoutLimits(SlideManager.activeSlide);

            if (!cameraLimits.zoomLimitEnabled) {
                Q3.controls.minDistance = 0;
                Q3.controls.maxDistance = Infinity;
            } else {
                Q3.controls.minDistance = cameraLimits.minDistance;
                Q3.controls.maxDistance = cameraLimits.maxDistance;
            }

            if (!cameraLimits.horizontalAngleLimitEnabled) {
                Q3.controls.minAzimuthAngle = -Infinity;
                Q3.controls.maxAzimuthAngle = Infinity;
            } else {
                Q3.controls.minAzimuthAngle = cameraLimits.minAzimuthAngle * THREE.Math.DEG2RAD;
                Q3.controls.maxAzimuthAngle = cameraLimits.maxAzimuthAngle * THREE.Math.DEG2RAD;
            }

            if (!cameraLimits.verticalAngleLimitEnabled) {
                const _1deg = THREE.Math.DEG2RAD;
                Q3.controls.minPolarAngle = _1deg;
                Q3.controls.maxPolarAngle = Math.PI - _1deg;
            } else {
                Q3.controls.minPolarAngle = cameraLimits.minPolarAngle * THREE.Math.DEG2RAD;
                Q3.controls.maxPolarAngle = cameraLimits.maxPolarAngle * THREE.Math.DEG2RAD;
            }

            if (cameraLimits.panLimitEnabled) {
                const { center, quaternion, offset } = cameraLimits.panLimits;
                Q3.controls.setPanLimits(center, quaternion, offset);
            } else Q3.controls.setPanLimits();

            const scaleLimits = Q3.objectRotationControlAddons.scaleLimits;
            scaleLimits.enabled = cameraLimits.objectScaleLimitEnabled;
            scaleLimits.limit = cameraLimits.objectScaleLimit;

            clearTimeout(Q3.limitsTimeout);

            Q3.limitsTimeout = null;
        };

        clearTimeout(Q3.limitsTimeout);

        Q3.limitsTimeout = setTimeout(() => Q3.setCameraLimits(), duration * 1000);
    }

    function animateLayers() {
        // LAYERS VISIBILITY TWEEN
        // 1. tween the unvisible layers to invisible
        const currentSlide = SlideManager.currentSlide;
        const initialLayersVisibility = {};
        const finalLayersVisibility = {};
        const layers2D = Q3.data.layers.layers2D;
        const layer3D = Q3.data.layers.layer3D;
        const slide = SlideManager.getSlide(prevSlide);

        Object.keys(LayersManager.list).forEach(layerUuid => {
            let initialVisibility = slide.layers[layerUuid] !== false;
            let finalVisibility = currentSlide.layers[layerUuid] !== false;
            let layer = layers2D[layerUuid] || layer3D;

            while (initialVisibility && layer.parent) {
                let parentUuid = layer.parent;

                layer = layers2D[layer.parent];

                initialVisibility = slide.layers[parentUuid] !== false;
            }

            layer = layers2D[layerUuid] || layer3D;

            while (finalVisibility && layer.parent) {
                let parentUuid = layer.parent;

                layer = layers2D[layer.parent];

                finalVisibility = currentSlide.layers[parentUuid] !== false;
            }

            initialLayersVisibility[layerUuid] = initialVisibility;
            finalLayersVisibility[layerUuid] = finalVisibility;
        });
    }

    function animateMeshes() {
        const currentSlide = SlideManager.currentSlide;

        let actRefMap = null;
        const getActRefMap = () => {
            if (!actRefMap) {
                const tourActContIt = iterateTourActionContainers(Q3.data);
                const slideActContIt = iterateSlideActionContainers(currentSlide);
                const actContIt = chainIterators([tourActContIt, slideActContIt]);
                actRefMap = new ActionReferenceMap(iterateActions(actContIt));
            }

            return actRefMap;
        };

        const isObjectInActions = objectUuid => getActRefMap().isObjectReferredByActions(objectUuid, "unhide3d");
        const isObjectVisible = (objectUuid, animation) => animation.visible !== false || isObjectInActions(objectUuid);

        // animation mixer
        Object.keys(currentSlide.animation).forEach(objectUuid => {
            const animation = currentSlide.animation[objectUuid];

            if (!animation) return;

            const objectAnimation = "animation" in animation
                ? animation.animation
                : Q3.data.assets.objects[objectUuid] && "animation" in Q3.data.assets.objects[objectUuid]
                    ? Q3.data.assets.objects[objectUuid].animation
                    : null;

            if (objectAnimation && objectAnimation.uuid && isObjectVisible(objectUuid, animation)) {
                if (objectAnimation.isPreset) {
                    Q3.mixer.createAndPlayPresetAnimation(Object.assign({
                        objectUuid,
                        animationUuid: objectAnimation.uuid,
                        loopType: objectAnimation.type,
                        timescale: objectAnimation.timescale,
                    }, objectAnimation.preset));
                } else if (AssetLibrary.animations[objectAnimation.uuid]) {
                    Q3.mixer.playObjectAnimation({
                        objectUuid,
                        animationUuid: objectAnimation.uuid,
                        loopType: objectAnimation.type,
                        timescale: objectAnimation.timescale,
                    });
                }
            } else {
                const slideQuiz = currentSlide.slideQuiz;

                function checkIsPloppable() {
                    return slideQuiz.meshes.some(plop => {
                        let object = AssetLibrary.objects[plop.uuid];

                        if (!object) return false;

                        do {
                            if (object.uuid === objectUuid) return true;

                            object = object.parent;

                        } while (object && !object.isHiddenRoot && object !== RenderManager.activeScene);

                        return false;
                    });
                }

                const isPloppable = slideQuiz && slideQuiz.type === "plopQuiz" && checkIsPloppable();

                if (objectAnimation)
                    Q3.mixer.stopObjectCurrentAnimation(objectUuid, animation.visible !== false && !isPloppable ? 1 : -1);
            }
        });
    }

    function updateObjectVisibility() {
        const currentSlide = SlideManager.currentSlide;

        [...Object.keys(AssetLibrary.objects), ...Object.keys(AssetLibrary.lights)].forEach(objectUuid => {
            const anim = currentSlide.animation[objectUuid];

            if (anim) {
                const object = (AssetLibrary.objects[objectUuid] || AssetLibrary.lights[objectUuid]);
                const isVisible = !(anim.visible === false) && anim.deleted !== true;
                object.visible = Q3.TransformModifiers.isTransformModifier(object) || isVisible;
            }
        });
    }

    function wheelHandler(e) {
        e.stopImmediatePropagation();
        e.stopPropagation();

        if (e.deltaY > 0) goToNextVisibleSlide({ userAction: true })
        else if (e.deltaY < 0) goToPrevVisibleSlide({ userAction: true, wheelAction: true });
    }

    function setScrollHandler() {
        document.removeEventListener("wheel", wheelHandler);

        const curSlide = SlideManager.currentSlide;

        if (!Q3.editMode && curSlide.scrollAdvance)
            document.addEventListener("wheel", wheelHandler);
    }

    function triggerControlChanges(onCameraTransitionTrigger) {
        const curSlide = SlideManager.currentSlide;

        Q3.controls.autoCentering = curSlide.controls
            ? curSlide.controls.autoCentering
            : Boolean(Q3.data.setups.controls.autoCentering);

        if (curSlide.controls) {
            let interactionType = curSlide.controls.interactionType || 'orbit';

            if (interactionType !== 'orbit') Q3.physicalCameraControls.enabled = false;
            if (interactionType !== 'FPS') Q3.FPSControls.deactivate();
            if (interactionType !== 'objectRotation')
                Q3.objectRotationControls.enabled = false;

            let objectRotationMode = curSlide.controls.objectRotationMode || 'orbit';

            Q3.objectRotationControls.rotationMode = objectRotationMode;

            if (Q3.controls.isStaticControls) {
                const data = Q3.SlideManager.currentSlide.camera;

                RenderManager.clearCameraAsset();

                RenderManager.activeCamera.position.copy(data.position);
                RenderManager.activeCamera.lookAt(data.target.x, data.target.y, data.target.z);
            }

            if (!Q3.isAssetMode) {
                switch (interactionType) {
                    case 'orbit':
                        Q3.controls = Q3.physicalCameraControls;
                        Q3.controls.enabled = true;
                        break;
                    case 'FPS':
                        Q3.controls = Q3.FPSControls;
                        Q3.controls.activate();
                        break;
                    case 'objectRotation':
                        Q3.controls = Q3.objectRotationControls;
                        Q3.controls.enabled = true;
                        break;
                    case "asset":
                        Q3.controls = Q3.staticControls;
                        RenderManager.setAssetCamera(curSlide.controls.asset);
                        break;
                }
            } else {
                Q3.controls = Q3.objectRotationControls;
                Q3.controls.enabled = true;
            }

            for (let controls of [Q3.physicalCameraControls, Q3.FPSControls]) {
                controls.autoRotate = false;

                onCameraTransitionTrigger.push(() => {
                    controls.autoRotate = curSlide.controls.autoRotate;
                    controls.setAutoRotationDelay(curSlide.controls.autoRotateDelay || 3);
                });

                controls.autoRotateSpeed = curSlide.controls.autoRotateSpeed || 0.3;
            }
        }
    }

    function triggerMeshPlopChanges(slideIndex) {
        if (!Q3.editMode) return;

        const slideQuiz = SlideManager.getSlide(slideIndex).slideQuiz;

        if (slideQuiz && slideQuiz.type === "plopQuiz") {
            const meshes = slideQuiz.meshes;

            meshes.forEach((mesh, index, array) => {
                if (AssetLibrary.objects[mesh.uuid]) {
                    array[index] = AssessmentManager.createAssessment("mesh-plop", {
                        object: AssetLibrary.objects[mesh.uuid],
                        matrix: new THREE.Matrix4().fromArray(mesh.matrix),
                        sizeSm: {
                            matrix: new THREE.Matrix4().fromArray("sizeSm" in mesh ? mesh.sizeSm : mesh.matrix),
                        },
                        originalState: mesh.originalState
                    });
                }
            });
        }
    }

    function triggerUriChanges() {
        if ("URLSearchParams" in window && Q3.editMode) {
            const searchParams = new URLSearchParams(window.location.search);
            const uuid = SlideManager.getSlide(activeSlide).uuid;

            if (!editAssetMode)
                searchParams.set("slide", uuid ? uuid : activeSlide + 1);

            const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`;

            history.pushState(null, '', newRelativePathQuery);
        }
    }

    function animateCameraZoom() {
        const curSlide = SlideManager.currentSlide;
        const progress = { value: 0 };
        const setups = Q3.data.setups;

        TweenLite.to(progress, 2.5, {
            value: 1,
            ease: Power0.easeNone
        });

        RenderManager.activeCamera.update = true;

        Q3.controls.zoomSpeed = curSlide.controls.zoomSpeed && setups.controls.perSlideZoomSpeed
            ? curSlide.controls.zoomSpeed
            : setups.controls.zoomSpeed;
    }

    function animateCanvas(slideIndex, duration) {
        // VIEWPORT/SCISSOR + FOV TWEEN
        const currentSlide = SlideManager.currentSlide;
        const slide = SlideManager.getSlide(prevSlide);
        const prevKey = getVWKey() === "sizeSm" && slide.rendererSm
            ? "rendererSm"
            : "renderer";

        const slideRenderer = slide[prevKey];
        const rData = setRenderData({
            width: slideRenderer.width,
            height: slideRenderer.height,
            left: slideRenderer.left,
            top: slideRenderer.top,
            lerping: true,
            vpLeft: NaN,
            vpTop: NaN,
            vpWidth: NaN,
            vpHeight: NaN,
        });

        const getCurrentLetterbox = (function () {
            let letterbox = null;
            return function () {
                letterbox = letterbox || getLetterbox();
                return letterbox;
            }
        })();

        if (Q3.isReady) {
            QuizManager.beforeTransition();

            if (!Q3.editMode && !QuizManager.isQuiz3D(slideIndex))
                QuizManager.initSlideQuiz(slideIndex);

            if (!Q3.editMode)
                setNextPrevEnabled({ next: false, prev: false });
        }

        // isTransitioning prevents updating the thumbnails in the editor, which was making the transitions stutter.
        Q3.isTransitioning = true;
        const newKey = getVWKey() === "sizeSm" && currentSlide.rendererSm ? "rendererSm" : "renderer";
        const rDataDesired = currentSlide[newKey];
        const wasResized = !Q3.editMode && (rData.top > 1e-3 || rData.left > 1e-3 || rData.width < 1 - 1e-3 || rData.height < 1 - 1e-3);
        const willBeResized = !Q3.editMode && (rDataDesired.top > 1e-3 || rDataDesired.left > 1e-3 || rDataDesired.width < 1 - 1e-3 || rDataDesired.height < 1 - 1e-3);
        const { width: canvasWidth, height: canvasHeight } = RenderManager.getSize();
        const curViewbox = !wasResized
            ? { x: 0, y: 0, w: canvasWidth, h: canvasHeight }
            : getCurrentLetterbox();
        const nextViewbox = !willBeResized
            ? { x: 0, y: 0, w: canvasWidth, h: canvasHeight }
            : getCurrentLetterbox();

        Object.assign(rData, {
            vpLeft: curViewbox.x,
            vpTop: curViewbox.y,
            vpWidth: curViewbox.w,
            vpHeight: curViewbox.h
        });

        resize();

        TweenLite.to(rData, duration, {
            width: rDataDesired.width,
            height: rDataDesired.height,
            left: rDataDesired.left,
            top: rDataDesired.top,
            vpLeft: nextViewbox.x,
            vpTop: nextViewbox.y,
            vpWidth: nextViewbox.w,
            vpHeight: nextViewbox.h,

            onUpdate() { resize(); },

            onComplete() {
                setRenderData({ lerping: false });
                Q3.isTransitioning = false;

                if (!Q3.editMode) {
                    const nextEnabled = Q3.SlideManager.activeSlide < Q3.SlideManager.slideCount - 1
                    setNextPrevEnabled({ next: nextEnabled, prev: true });
                }

                RenderManager.raycaster.enabled = true;
                RenderManager.activeCamera.update = true;

                if (Q3.isReady && !Q3.editMode && QuizManager.isQuiz3D(slideIndex))
                    QuizManager.initSlideQuiz(slideIndex);

                const curSlide = SlideManager.getSlide(slideIndex);
                if (Q3.editMode && curSlide) {
                    const slideQuiz = curSlide.slideQuiz;

                    if (slideQuiz && slideQuiz.active && (["isolated", "draglabels"].indexOf(slideQuiz.type)) !== -1 && slideQuiz.meshes.length)
                        Q3.MeshIsolation.show(slideQuiz.meshes.map(({ uuid }) => uuid).flat());
                    else Q3.MeshIsolation.end();
                }

                setTimeout(() => RenderManager.activeCamera.update = true, 250);
                setTimeout(() => RenderManager.activeCamera.update = true, 500);

                resize();
            }
        });
    }

    return async function commitToSlide(slideIndex, opts) {
        const currentSlide = SlideManager.currentSlide;

        if (Q3.controls.cancelDamping) Q3.controls.cancelDamping();

        clearAssessments();

        Q3.controls.lastSetByUserPosition = null;

        if (Q3.selected && Q3.selected.type === "2DObject") fire("unselect");

        Q3.mediaIsPlaying = [];

        if (!Q3.editMode && callbacks.length) {
            callbacks.forEach(cb => {
                if (typeof cb === "function")
                    cb();
            });

            callbacks = [];
        }

        deleteSlideLayers(prevSlide, opts);

        const anim2dDelaysOnSlide = createNewLayers();
        const anim3dDelaysOnSlide = Object.values(currentSlide.animation).find(obj => {
            if (obj) {
                return obj.visible !== false && obj.animation && obj.animation.preset && obj.animation.preset.delay
            } else {
                return false;
            }
        })
        if (!Q3.inPreviewMode && (anim2dDelaysOnSlide || anim3dDelaysOnSlide)) {
            Q3.showSnackBar("Entrance delay only applied for viewer mode");
        }
        removeCameraLimits();

        const envData = await EnvLight.loadEnvLight(currentSlide.envLight);
        await EnvLight.setEnvLight(envData);

        if (Q3.animations[prevSlide]) Q3.animations[prevSlide].kill();
        Q3.objectRotationControlAddons.autoSpin.clearAutoSpin();
        Q3.objectRotationControls.clearObjectRotationSpaces();

        const onCameraTransitionTrigger = [];
        const slideTransitionDuration = 1;
        const duration = Q3.browserCapabilities.instantTransitions
            ? 0
            : Utils.queryParams.has("slide_duration")
                ? Utils.queryParams.getNumber("slide_duration")
                : (opts.noAnimation ? 0 : slideTransitionDuration) * (opts.quick ? 0.25 : 1);

        triggerMeshPlopChanges(slideIndex);
        animateMeshes();

        Q3.animations[activeSlide].start({
            duration: Q3.editMode ? 0 : duration,
            onTransitionComplete: () => {
                onCameraTransitionTrigger.forEach(callback => callback());
                Q3.objectRotationControlAddons.autoSpin.setAutoSpinFromSlide();
            },
        });

        triggerBackgroundTransitions(duration);

        /* FOG TRANSITION */
        RenderManager.fog.setSlideFog(activeSlide, duration);

        initCameraLimits(Q3.editMode ? 0 : duration);
        animateLayers();

        RenderManager.raycaster.enabled = false;
        RenderManager.raycaster.clear();

        LayersManager.update(undefined, opts.byScroll);
        fire("layersCreated");

        updateObjectVisibility();
        setScrollHandler();
        triggerControlChanges(onCameraTransitionTrigger);
        animateCanvas(slideIndex, duration);
        animateCameraZoom();

        const interactionType = currentSlide.controls && currentSlide.controls.interactionType || "orbit";

        Q3.controls.enabled = !(currentSlide.camera.disabled === true && interactionType !== "objectRotation");

        if (Q3.transformControls) Q3.transformControls.detach();

        triggerUriChanges();

        RenderManager.setHighlightObjects();

        if (!Q3.editMode) {
            AssetDeserializer.loadSlideQueue({
                data: Q3.data,
                slide: activeSlide + 1,
                assets: AssetLibrary
            });
        }

        if (opts.onReady) opts.onReady();
    }
})();

function setSlideViewer(slideIndex, opts) {
    console.log("Set slide viewer:", slideIndex + 1);

    const slideQuiz = SlideManager.currentSlide.slideQuiz;

    if (slideQuiz && slideQuiz.active) {
        slideQuiz.hints.filter(h => h.type === "object" && h.uuid.length).reduce((acc, h) => acc.concat(h.uuid), []).forEach((uuid) => {
            SlideManager.currentSlide.layers[uuid] = false;
            Object.values(Q3.data.layers.layers2D[uuid].objects)[0].hiddenMobile = true;
        })
    }
    if (AssetsBusy.isSlideReady(slideIndex) && activeSlide === slideIndex) {
        commitToSlide(slideIndex, opts);
        return;
    }

    if (commitListener) {
        unlisten("commit_slide", commitListener);
        commitListener = null;
    }

    commitListener = options => {
        unlisten("commit_slide", commitListener);
        commitListener = null;
        if (activeSlide === slideIndex) {
            console.log("Commiting to slide:", slideIndex + 1, activeSlide + 1);
            commitToSlide(slideIndex, Object.assign({}, options || {}, opts));
        }
    }

    listen("commit_slide", commitListener);
}

function deleteSlideLayers(slideIndex, opts = {}) {
    const layers2D = Q3.data.layers.layers2D;

    const slide = SlideManager.getSlide(slideIndex + Number(Boolean(opts.addedBefore)));

    // delete 2d layers of prev slide
    Object.keys(slide.layers2D).forEach(uuid => {
        if (layers2D[uuid] && LayersManager.list[uuid])
            LayersManager.list[uuid].delete();
    });
}
